import { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Search, X, User, UserX, UserMinus } from 'lucide-react';
import type { HeaderFilter, UserFilterSelection } from '../../../contexts/DynamicHeaderContext';
import { useAuth } from '../../../lib/auth';
import { GradientCheckbox } from './GradientCheckbox';

/** "Non assegnata": nessun proprietario. */
export const UNASSIGNED_FILTER_ID = '__unassigned__';
/**
 * "Proprietario non piu' attivo": il lead ha un assegnatario, ma e' un utente
 * disattivato (o cancellato), quindi non compare piu' nel menu. Senza questa
 * voce quei lead si vedevano solo con "Tutti" e non si potevano isolare per
 * riassegnarli: chi lasciava l'azienda si portava via i lead dal filtro.
 */
export const INACTIVE_OWNER_FILTER_ID = '__inactive_owner__';

interface UserOption {
  id: string;
  full_name?: string;
  email: string;
  avatar_url?: string;
  profile_image_url?: string;
}

interface UserFilterDropdownProps {
  filter: HeaderFilter;
  users?: UserOption[];
}

export function UserFilterDropdown({ filter, users = [] }: UserFilterDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { user: currentUser } = useAuth();

  const selectedUsers: UserFilterSelection[] = Array.isArray(filter.value) ? filter.value : [];
  // "Tutti" e' la selezione vuota: nessun filtro, si vede ogni lead. E' l'unica
  // forma che regge anche quando la ricerca passa dal database, che non sa
  // esprimere "tutti i nomi piu' quelli senza proprietario".
  const tutti = selectedUsers.length === 0;

  const sortedUsers = useMemo(() => {
    return [...users].sort((a, b) => {
      if (currentUser?.id) {
        if (a.id === currentUser.id) return -1;
        if (b.id === currentUser.id) return 1;
      }
      const nameA = a.full_name || a.email || '';
      const nameB = b.full_name || b.email || '';
      return nameA.localeCompare(nameB);
    });
  }, [users, currentUser?.id]);

  const unassignedOption: UserOption = { id: UNASSIGNED_FILTER_ID, full_name: 'Non assegnata', email: '' };
  const inactiveOwnerOption: UserOption = { id: INACTIVE_OWNER_FILTER_ID, full_name: 'Proprietario non più attivo', email: '' };
  const specialOptions = useMemo(
    () => (filter.showInactiveOwner ? [unassignedOption, inactiveOwnerOption] : [unassignedOption]),
    [filter.showInactiveOwner],
  );
  const isSpecial = (id: string) => id === UNASSIGNED_FILTER_ID || id === INACTIVE_OWNER_FILTER_ID;

  const updatePosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const dropdownWidth = 288;
      const dropdownHeight = 460;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      let top = rect.bottom + 4;
      if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) top = rect.top - dropdownHeight - 4;
      const spaceRight = window.innerWidth - rect.left;
      let left = rect.left;
      if (spaceRight < dropdownWidth + 16) left = rect.right - dropdownWidth;
      left = Math.max(8, Math.min(left, window.innerWidth - dropdownWidth - 8));
      setPosition({ top: Math.max(8, top), left });
    }
  };

  const handleToggle = () => {
    if (!isOpen) updatePosition();
    setIsOpen(!isOpen);
    setSearchQuery('');
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        buttonRef.current && !buttonRef.current.contains(e.target as Node) &&
        dropdownRef.current && !dropdownRef.current.contains(e.target as Node)
      ) setIsOpen(false);
    };
    const handleEscape = (e: KeyboardEvent) => { if (e.key === 'Escape') setIsOpen(false); };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen]);

  /** Riporta al proprio nome: e' il punto di partenza di una scelta a mano. */
  const soloIMiei = () => {
    if (currentUser?.id) {
      filter.onChange([{ userId: currentUser.id, mode: 'both' as const }]);
    } else {
      filter.onChange([]);
    }
  };

  /**
   * L'interruttore "Tutti": acceso = nessun filtro e l'elenco dei nomi sparisce,
   * perche' non c'e' niente da spuntare; spento = si torna a scegliere, a
   * partire dal proprio nome.
   */
  const toggleTutti = () => {
    if (tutti) soloIMiei();
    else filter.onChange([]);
  };

  const toggleUser = (userId: string) => {
    const existingIndex = selectedUsers.findIndex(su => su.userId === userId);
    if (existingIndex >= 0) {
      const newSelection = selectedUsers.filter(su => su.userId !== userId);
      if (newSelection.length === 0) {
        // Togliere l'ultimo nome non vuol dire "tutti": si resta sul proprio.
        soloIMiei();
      } else {
        filter.onChange(newSelection);
      }
    } else {
      filter.onChange([...selectedUsers, { userId, mode: 'both' as const }]);
    }
  };

  const changeUserMode = (userId: string, mode: 'assigned_to' | 'sales_rep_id' | 'both') => {
    filter.onChange(selectedUsers.map(su => su.userId === userId ? { ...su, mode } : su));
  };

  const filteredUsers = useMemo(() => {
    const q = searchQuery.toLowerCase();
    const filtered = q
      ? sortedUsers.filter(u =>
          (u.full_name?.toLowerCase().includes(q)) ||
          (u.email?.toLowerCase().includes(q))
        )
      : sortedUsers;
    const speciali = q
      ? specialOptions.filter(o => (o.full_name || '').toLowerCase().includes(q))
      : specialOptions;
    return [...speciali, ...filtered];
  }, [sortedUsers, searchQuery, specialOptions]);

  const getInitials = (user: UserOption) => {
    if (user.full_name) {
      const parts = user.full_name.split(' ');
      if (parts.length >= 2) return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
      return user.full_name.substring(0, 2).toUpperCase();
    }
    return user.email?.substring(0, 2).toUpperCase() || '??';
  };

  const nomeDi = (id: string) => {
    if (id === UNASSIGNED_FILTER_ID) return 'Non assegnata';
    if (id === INACTIVE_OWNER_FILTER_ID) return 'Non più attivo';
    const u = users.find(u => u.id === id);
    return u?.full_name || u?.email || filter.label;
  };

  // L'etichetta in barra dice sempre cosa sta succedendo, anche a tendina
  // chiusa: "Tutti" quando non c'e' filtro, il nome quando e' uno, il numero
  // quando sono di piu'.
  const getButtonLabel = () => {
    if (tutti) return `${filter.label}: Tutti`;
    if (selectedUsers.length === 1) return nomeDi(selectedUsers[0].userId);
    return `${filter.label}: ${selectedUsers.length}`;
  };

  const hasSelection = !tutti;

  const gradientTextStyle: React.CSSProperties = {
    background: 'linear-gradient(135deg, #E91E8C, #F97316)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  };

  const modeActiveClass = 'text-white';
  const modeInactiveClass = 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600';

  return (
    <>
      <button
        ref={buttonRef}
        onClick={handleToggle}
        className={`inline-flex items-center gap-1.5 px-2.5 h-7 text-xs rounded-md focus:outline-none transition-colors ${
          hasSelection
            ? 'bg-[rgba(233,30,140,0.08)] hover:bg-[rgba(233,30,140,0.13)]'
            : 'text-foreground hover:bg-black/5 dark:hover:bg-white/5'
        }`}
      >
        <User className={`w-3 h-3 flex-shrink-0 ${hasSelection ? 'text-[#E91E8C]' : 'text-muted-foreground'}`} />
        <span className="truncate max-w-[180px]" style={hasSelection ? gradientTextStyle : {}}>
          {getButtonLabel()}
        </span>
        <ChevronDown className={`w-3 h-3 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''} ${hasSelection ? 'text-[#E91E8C]' : 'text-muted-foreground'}`} />
      </button>

      {isOpen && createPortal(
        <div
          ref={dropdownRef}
          className="fixed w-72 max-h-[460px] rounded-lg shadow-xl bg-white dark:bg-gray-800 ring-1 ring-gray-200 dark:ring-white/10 z-[20050] flex flex-col"
          style={{ top: position.top, left: position.left }}
        >
          {/* L'interruttore "Tutti": una riga sola, piccola come le altre. */}
          <button
            type="button"
            role="switch"
            aria-checked={tutti}
            onClick={toggleTutti}
            title="Ogni lead, anche senza proprietario o di utenti non più attivi"
            className="flex items-center justify-between gap-2 px-3 py-1.5 border-b border-gray-200 dark:border-gray-700 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
          >
            <span className={`text-xs ${tutti ? 'font-medium text-gray-800 dark:text-gray-100' : 'text-gray-600 dark:text-gray-300'}`}>
              Tutti, anche senza proprietario
            </span>
            <span
              className={`relative inline-flex h-3.5 w-6 flex-shrink-0 items-center rounded-full transition-colors ${
                tutti ? '' : 'bg-gray-300 dark:bg-gray-600'
              }`}
              style={tutti ? { background: 'linear-gradient(135deg, #E91E8C, #F97316)' } : undefined}
            >
              <span
                className={`inline-block h-2.5 w-2.5 rounded-full bg-white shadow transition-transform ${
                  tutti ? 'translate-x-[11px]' : 'translate-x-0.5'
                }`}
              />
            </span>
          </button>

          {tutti ? (
            <div className="px-3 py-3 text-xs text-gray-500 dark:text-gray-400">
              Nessun filtro: vedi tutto. Spegni l'interruttore per scegliere le persone.
            </div>
          ) : (
            <>
              <div className="p-2 border-b border-gray-200 dark:border-gray-700">
                <div className="relative mb-2">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Cerca utente..."
                    className="w-full pl-8 pr-8 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-[#E91E8C]"
                    autoFocus
                  />
                  {searchQuery && (
                    <button onClick={() => setSearchQuery('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <div className="flex items-center justify-end text-xs">
                  <button onClick={soloIMiei} className="text-muted-foreground hover:underline">
                    Solo i miei
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-1">
                {filteredUsers.length === 0 ? (
                  <div className="px-3 py-4 text-sm text-gray-500 text-center">
                    {searchQuery ? 'Nessun utente trovato' : 'Nessun utente disponibile'}
                  </div>
                ) : (
                  filteredUsers.map(user => {
                    const speciale = isSpecial(user.id);
                    const ultimaSpeciale = speciale && user.id === specialOptions[specialOptions.length - 1].id;
                    const userSelection = selectedUsers.find(su => su.userId === user.id);
                    const isSelected = !!userSelection;
                    const currentMode = userSelection?.mode || 'both';
                    const userImage = user.avatar_url || user.profile_image_url;

                    return (
                      <div key={user.id} className={`px-2.5 py-2 ${ultimaSpeciale ? 'border-b border-gray-200 dark:border-gray-700' : ''}`}>
                        <div
                          className="flex items-center gap-2.5 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 rounded -mx-1.5 px-1.5 py-1 transition-colors"
                          onClick={() => toggleUser(user.id)}
                        >
                          <GradientCheckbox checked={isSelected} />

                          <div className={`w-7 h-7 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0 ${
                            speciale ? 'bg-gray-300 dark:bg-gray-500' : 'bg-gray-200 dark:bg-gray-600'
                          }`}>
                            {user.id === UNASSIGNED_FILTER_ID ? (
                              <UserX className="w-4 h-4 text-gray-500 dark:text-gray-300" />
                            ) : user.id === INACTIVE_OWNER_FILTER_ID ? (
                              <UserMinus className="w-4 h-4 text-gray-500 dark:text-gray-300" />
                            ) : userImage ? (
                              <img src={userImage} alt={user.full_name || user.email} className="w-full h-full object-cover" />
                            ) : (
                              <span className="text-xs font-medium text-gray-600 dark:text-gray-300">
                                {getInitials(user)}
                              </span>
                            )}
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className={`text-sm truncate ${
                              speciale
                                ? 'text-gray-500 dark:text-gray-400 italic'
                                : 'text-gray-700 dark:text-gray-200'
                            }`}>
                              {user.full_name || user.email}
                            </div>
                            {user.full_name && user.email && !speciale && (
                              <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{user.email}</div>
                            )}
                          </div>
                        </div>

                        {isSelected && !speciale && (
                          <div className="mt-1.5 ml-6 flex gap-1">
                            {(['assigned_to', 'sales_rep_id', 'both'] as const).map((mode) => (
                              <button
                                key={mode}
                                onClick={(e) => { e.stopPropagation(); changeUserMode(user.id, mode); }}
                                className={`flex-1 px-1.5 py-0.5 text-[10px] font-medium rounded transition-colors ${
                                  currentMode === mode ? modeActiveClass : modeInactiveClass
                                }`}
                                style={currentMode === mode ? {
                                  background: 'linear-gradient(135deg, #E91E8C, #F97316)',
                                } : {}}
                              >
                                {mode === 'assigned_to' ? 'Assegn.' : mode === 'sales_rep_id' ? 'Comm.' : 'Entrambi'}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>

              <div className="px-3 py-2 border-t border-gray-200 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400">
                {`${selectedUsers.length} di ${users.length + specialOptions.length} selezionati`}
              </div>
            </>
          )}
        </div>,
        document.body
      )}
    </>
  );
}
